import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface DualRingDonutChartProps {
  baseYearData: {
    scope1: number;
    scope2: number;
    scope3: number;
    total: number;
  } | null;
  reportingYearData: {
    scope1: number;
    scope2: number;
    scope3: number;
    total: number;
  } | null;
  baseYear: number;
  reportingYear: number;
}

interface ScopeData {
  name: string;
  value: number;
  percentage: number;
  color: string;
  fill: string;
}

export function DualRingDonutChart({ 
  baseYearData, 
  reportingYearData, 
  baseYear, 
  reportingYear 
}: DualRingDonutChartProps) {
  // Always show the chart, even if data is missing
  // Use default values if data is not available

  // Professional color scheme - harmonious and contrasting
  const scopeColors = {
    scope1: {
      base: '#1e40af',    // Deep blue for base year
      reporting: '#3b82f6' // Lighter blue for reporting year
    },
    scope2: {
      base: '#059669',    // Deep green for base year
      reporting: '#10b981' // Lighter green for reporting year
    },
    scope3: {
      base: '#7c2d12',    // Deep orange for base year
      reporting: '#f97316' // Lighter orange for reporting year
    }
  };

  // Prepare data for inner ring (Base Year)
  const baseYearChartData: ScopeData[] = [
    {
      name: 'Scope 1 (Direct)',
      value: baseYearData?.scope1 || 0,
      percentage: baseYearData?.total ? (baseYearData.scope1 / baseYearData.total) * 100 : 0,
      color: scopeColors.scope1.base,
      fill: scopeColors.scope1.base
    },
    {
      name: 'Scope 2 (Energy)',
      value: baseYearData?.scope2 || 0,
      percentage: baseYearData?.total ? (baseYearData.scope2 / baseYearData.total) * 100 : 0,
      color: scopeColors.scope2.base,
      fill: scopeColors.scope2.base
    },
    {
      name: 'Scope 3 (Other)',
      value: baseYearData?.scope3 || 0,
      percentage: baseYearData?.total ? (baseYearData.scope3 / baseYearData.total) * 100 : 0,
      color: scopeColors.scope3.base,
      fill: scopeColors.scope3.base
    }
  ].filter(item => item.value > 0);

  // Prepare data for outer ring (Reporting Year)
  // Always show the outer ring, even if no data is available
  const reportingYearChartData: ScopeData[] = [
    {
      name: 'Scope 1 (Direct)',
      value: reportingYearData?.scope1 || 0,
      percentage: reportingYearData?.total ? (reportingYearData.scope1 / reportingYearData.total) * 100 : 0,
      color: scopeColors.scope1.reporting,
      fill: scopeColors.scope1.reporting
    },
    {
      name: 'Scope 2 (Energy)',
      value: reportingYearData?.scope2 || 0,
      percentage: reportingYearData?.total ? (reportingYearData.scope2 / reportingYearData.total) * 100 : 0,
      color: scopeColors.scope2.reporting,
      fill: scopeColors.scope2.reporting
    },
    {
      name: 'Scope 3 (Other)',
      value: reportingYearData?.scope3 || 0,
      percentage: reportingYearData?.total ? (reportingYearData.scope3 / reportingYearData.total) * 100 : 0,
      color: scopeColors.scope3.reporting,
      fill: scopeColors.scope3.reporting
    }
  ];
  
  // If no reporting year data is available, show placeholder data
  const hasReportingData = reportingYearData && reportingYearData.total > 0;
  const finalReportingData = hasReportingData ? reportingYearChartData : [
    {
      name: 'No Data Available',
      value: 1, // Small value to show a ring
      percentage: 100,
      color: '#e5e7eb', // Light gray
      fill: '#e5e7eb'
    }
  ];

  // Enhanced custom label component with better formatting
  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, value, isOuter }: any) => {
    if (percent < 0.08) return null; // Don't show labels for segments < 8%
    
    const RADIAN = Math.PI / 180;
    const radius = isOuter ? innerRadius + (outerRadius - innerRadius) * 0.5 : innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Determine label position and alignment
    const isLeftSide = x < cx;
    const textAnchor = isLeftSide ? 'end' : 'start';
    const labelOffset = isLeftSide ? -8 : 8;
    
    // Format the value for display
    const formattedValue = value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(0);
    
    return (
      <g>
        {/* Background circle for better readability */}
        <circle
          cx={x + labelOffset}
          cy={y}
          r="12"
          fill="white"
          fillOpacity="0.9"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
        {/* Percentage label */}
        <text 
          x={x + labelOffset} 
          y={y - 3} 
          fill="#1f2937" 
          textAnchor="middle" 
          dominantBaseline="central"
          fontSize="8"
          fontWeight="700"
        >
          {`${(percent * 100).toFixed(1)}%`}
        </text>
        {/* Value label */}
        <text 
          x={x + labelOffset} 
          y={y + 6} 
          fill="#6b7280" 
          textAnchor="middle" 
          dominantBaseline="central"
          fontSize="7"
          fontWeight="500"
        >
          {`${formattedValue}t`}
        </text>
      </g>
    );
  };

  // Enhanced custom tooltip with more detailed information
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ScopeData;
      const isOuter = payload[0].dataKey === 'outerValue';
      const year = isOuter ? reportingYear : baseYear;
      const ringType = isOuter ? 'Reporting Year' : 'Base Year';
      
      // Calculate change if we have both base and reporting data
      let changeInfo = null;
      if (isOuter && hasReportingData && baseYearData) {
        const baseValue = baseYearData[data.name.toLowerCase().replace(' ', '').replace('(', '').replace(')', '') as keyof typeof baseYearData] as number;
        if (baseValue && baseValue > 0) {
          const change = ((data.value - baseValue) / baseValue) * 100;
          changeInfo = {
            value: change,
            isIncrease: change > 0,
            baseValue: baseValue
          };
        }
      }
      
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg max-w-xs">
          <div className="flex items-center space-x-2 mb-3">
            <div 
              className="w-4 h-4 rounded-full border-2 border-white shadow-sm" 
              style={{ backgroundColor: data.color }}
            />
            <div>
              <p className="font-semibold text-gray-900">{data.name}</p>
              <p className="text-xs text-gray-500">{ringType} ({year})</p>
            </div>
          </div>
          
          {hasReportingData || !isOuter ? (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Emissions:</span>
                <span className="font-semibold text-gray-900">{(data.value / 1000).toFixed(2)}t CO₂e</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Percentage:</span>
                <span className="font-semibold text-gray-900">{data.percentage.toFixed(1)}%</span>
              </div>
              
              {changeInfo && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Change from base:</span>
                    <span className={`font-semibold ${
                      changeInfo.isIncrease ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {changeInfo.isIncrease ? '+' : ''}{changeInfo.value.toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Base: {(changeInfo.baseValue / 1000).toFixed(2)}t → Current: {(data.value / 1000).toFixed(2)}t
                  </div>
                </div>
              )}
              
              {data.value === 0 && (
                <p className="text-xs text-orange-600 mt-2 p-2 bg-orange-50 rounded">
                  No data available for this year
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-orange-600 p-2 bg-orange-50 rounded">
              No data available for {year}
            </p>
          )}
        </div>
      );
    }
    return null;
  };


  // Show message if no base year data is available (we always show the chart now)
  if (!baseYearChartData || baseYearChartData.length === 0) {
    return (
      <div className="w-full">
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 text-gray-400">
              <i data-feather="pie-chart" className="w-12 h-12"></i>
            </div>
            <p className="text-gray-500 font-medium">No Base Year Data Available</p>
            <p className="text-sm text-gray-400 mt-1">
              Select a company with base year data to view scope comparison
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0">
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          {/* Inner Ring - Base Year (only if data exists) */}
          {baseYearChartData && baseYearChartData.length > 0 && (
            <Pie
              data={baseYearChartData}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
              stroke="#ffffff"
              strokeWidth={2}
              label={(props) => <CustomLabel {...props} isOuter={false} />}
              labelLine={false}
            >
              {baseYearChartData.map((entry, index) => (
                <Cell key={`base-cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          )}

          {/* Outer Ring - Reporting Year (always show) */}
          <Pie
            data={finalReportingData}
            cx="50%"
            cy="50%"
            innerRadius={80}
            outerRadius={110}
            paddingAngle={2}
            dataKey="value"
            stroke="#ffffff"
            strokeWidth={2}
            label={(props) => hasReportingData ? <CustomLabel {...props} isOuter={true} /> : null}
            labelLine={false}
          >
            {finalReportingData.map((entry, index) => (
              <Cell key={`reporting-cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>

          {/* Center Labels - Better positioned to avoid overlap */}
          <text
            x="50%"
            y="35%"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="11"
            fontWeight="600"
            fill="#374151"
          >
            Base Year
          </text>
          <text
            x="50%"
            y="42%"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fontWeight="500"
            fill="#6b7280"
          >
            {baseYear}
          </text>
          <text
            x="50%"
            y="58%"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="11"
            fontWeight="600"
            fill="#374151"
          >
            Reporting Year
          </text>
          <text
            x="50%"
            y="65%"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fontWeight="500"
            fill="#6b7280"
          >
            {reportingYear}
          </text>

          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      
      {/* Enhanced Legend with Ring Indicators */}
      <div className="flex flex-col space-y-3 mt-3">
        {/* Ring Labels */}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-white"></div>
            <span className="text-sm font-semibold text-gray-800">Base Year ({baseYear})</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full border-2 border-gray-600 bg-white"></div>
            <span className="text-sm font-semibold text-gray-800">Reporting Year ({reportingYear})</span>
          </div>
        </div>

        {/* Scope Legend */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[
            { 
              name: 'Scope 1 (Direct)', 
              baseColor: scopeColors.scope1.base, 
              reportingColor: scopeColors.scope1.reporting,
              baseValue: baseYearData?.scope1 || 0,
              reportingValue: hasReportingData ? (reportingYearData?.scope1 || 0) : 0
            },
            { 
              name: 'Scope 2 (Energy)', 
              baseColor: scopeColors.scope2.base, 
              reportingColor: scopeColors.scope2.reporting,
              baseValue: baseYearData?.scope2 || 0,
              reportingValue: hasReportingData ? (reportingYearData?.scope2 || 0) : 0
            },
            { 
              name: 'Scope 3 (Other)', 
              baseColor: scopeColors.scope3.base, 
              reportingColor: scopeColors.scope3.reporting,
              baseValue: baseYearData?.scope3 || 0,
              reportingValue: hasReportingData ? (reportingYearData?.scope3 || 0) : 0
            }
          ].map((scope, index) => (
            <div key={index} className="flex flex-col bg-gray-50 p-3 rounded-lg border border-gray-200 h-full">
              <div className="flex items-center space-x-2 mb-2">
                <div 
                  className="w-4 h-4 rounded-full border-2 border-white shadow-sm" 
                  style={{ backgroundColor: scope.baseColor }}
                  title={`Base Year ${scope.name}`}
                />
                <div 
                  className="w-4 h-4 rounded-full border-2 border-white shadow-sm" 
                  style={{ backgroundColor: scope.reportingColor }}
                  title={`Reporting Year ${scope.name}`}
                />
                <span className="text-xs font-semibold text-gray-800">{scope.name}</span>
              </div>
              <div className="text-xs text-gray-500">
                <div><span className="font-medium">Base:</span> {(scope.baseValue / 1000).toFixed(1)}t</div>
                <div><span className="font-medium">Reporting:</span> {hasReportingData ? `${(scope.reportingValue / 1000).toFixed(1)}t` : 'No Data'}</div>
              </div>
              <div className={`text-sm font-semibold mt-2 ${
                scope.reportingValue > scope.baseValue ? 'text-red-600' : 
                scope.reportingValue < scope.baseValue ? 'text-green-600' : 'text-gray-600'
              }`}>
                {hasReportingData ? (
                  scope.baseValue > 0 ? 
                    `${(((scope.reportingValue - scope.baseValue) / scope.baseValue) * 100).toFixed(1)}%` :
                    scope.reportingValue > 0 ? '+100%' : '0%'
                ) : 'N/A'}
              </div>
            </div>
          ))}
        </div>

        {/* Chart Description */}
        <div className="text-center">
          <p className="text-xs text-gray-500 italic">
            Inner ring represents base year emissions distribution. Outer ring shows reporting year changes.
          </p>
        </div>
      </div>
    </div>
  );
}
